import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import fm from "front-matter";
import remarkGfm from "remark-gfm";
import useScrollToTop from "../hooks/useScrollToTop";

interface Frontmatter {
  title: string;
  author: string;
  date: string;
  description?: string;
  image?: string;
}

import { Helmet } from "react-helmet";
import HyperLayout from "./HyperLayout";
import HeroSection from "./HeroSection";

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [content, setContent] = useState("");
  const [frontmatter, setFrontmatter] = useState<Frontmatter | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Use scroll to top hook
  useScrollToTop();

  useEffect(() => {
    if (!slug) return;
    fetch(`/blog/${slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error("Blog post not found");
        return res.text();
      })
      .then((raw) => {
        const parsed = fm<Frontmatter>(raw);
        setFrontmatter(parsed.attributes);
        setContent(parsed.body);
      })
      .catch((err) => setError(err.message));
  }, [slug]);

  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!content || !frontmatter) return <div>Loading...</div>;

  return (
    <HyperLayout hideBanner={true}>
      <Helmet>
        <title>{frontmatter.title} | BasedApp Blog</title>
        <meta
          name="description"
          content={frontmatter.description || content.substring(0, 160)}
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: frontmatter.title,
            description: frontmatter.description || content.substring(0, 160),
            datePublished: frontmatter.date,
            author: { "@type": "Person", name: frontmatter.author },
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id":
                typeof window !== "undefined" && window.location
                  ? window.location.href
                  : "",
            },
            image: frontmatter.image ? [frontmatter.image] : [],
          })}
        </script>
      </Helmet>
      <article>
        <HeroSection>
          <div className="max-w-3xl mx-auto flex flex-col mt-12 w-full relative z-30">
            <a
              href="/"
              className="text-sm text-primary/80 hover:text-primary font-medium rounded-full transition-colors mb-3 self-start">
              ← View All Posts
            </a>
            <h1 className="text-4xl sm:text-5xl font-heavy text-white leading-tight font-sans mb-3">
              {frontmatter.title}
            </h1>
            <div className="text-white/60 mb-8 text-base font-sans">
              {(() => {
                const d = new Date(frontmatter.date);
                return isNaN(d.getTime())
                  ? frontmatter.date
                  : d.toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
              })()}
              <span className="mx-2">·</span>By{" "}
              <span>{frontmatter.author}</span>
            </div>
          </div>
        </HeroSection>
        {frontmatter.image && (
          <div className="mb-8 max-w-3xl w-full mx-auto">
            <img
              src={frontmatter.image}
              alt={frontmatter.title}
              className="w-full shadow-md rounded-2xl overflow-hidden max-h-[500px] object-contain"
            />
          </div>
        )}
        <div className="pb-12 md:pb-24 prose max-w-3xl font-sans text-xl text-white leading-loose mx-auto prose-table:w-full prose-table:border-collapse prose-td:border prose-td:border-white prose-td:p-2 prose-th:border prose-th:border-white prose-th:p-2 prose-th:bg-white">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ node, ...props }) => (
                <h1
                  className="text-3xl sm:text-4xl font-bold text-white mt-8 mb-4 text-left"
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h2
                  className="text-2xl sm:text-3xl font-semibold text-white mt-6 mb-3 text-left"
                  {...props}
                />
              ),
              table: ({ node, ...props }) => (
                <table className="w-full border-collapse my-6" {...props} />
              ),
              th: ({ node, ...props }) => (
                <th
                  className="border border-gray-300 bg-gray-100 p-2 text-left"
                  {...props}
                />
              ),
              td: ({ node, ...props }) => (
                <td className="border border-gray-300 p-2" {...props} />
              ),
              a: ({ node, ...props }) => (
                <a className="text-primary" {...props} />
              ),
              strong: ({ node, ...props }) => (
                <strong className="text-bold text-white" {...props} />
              ),
              img: ({ node, ...props }) => (
                <img
                  className="w-full h-auto rounded-lg shadow-md my-6"
                  {...props}
                />
              ),
            }}>
            {content}
          </ReactMarkdown>
        </div>
      </article>
    </HyperLayout>
  );
};

export default BlogPost;
